import arrowLeftIcon from "@loot-os/icons/icons/arrow-left";
import arrowRightIcon from "@loot-os/icons/icons/arrow-right";
import plusIcon from "@loot-os/icons/icons/plus";
import trashIcon from "@loot-os/icons/icons/trash";
import { useId, useState } from "react";

import { Button } from "../../../../../core/Button.js";
import { cardStyles } from "../../../../../core/Card.styles.js";
import { Icon } from "../../../../../core/Icon.js";
import { IconButton } from "../../../../../core/IconButton.js";
import { Modal, ModalClose, ModalFooter } from "../../../../../core/Modal.js";
import { Skeleton } from "../../../../../core/Skeleton.js";
import { Text } from "../../../../../core/Text.js";
import { BattleBoxPick } from "../../../../../domain/battles/BattleBoxPick.js";
import { cn } from "../../../../../foundations/cn.js";
import { FOCUS_RING } from "../../../../../foundations/interaction.js";
import { LoadError } from "../../../../shared/LoadError.js";
import type {
  BattleBoxPickContent,
  BattlePicksContent,
  PickControl,
} from "../../../battlesContent.js";

const GRID = "grid grid-cols-2 gap-3 sm:grid-cols-3 lg:grid-cols-4";

/**
 * The boxes in the battle, in the order they open, each with its rounds stepper and the controls
 * to move or remove it; then a dashed "+ Add" card that opens the store's boxes to pick from.
 * The picker is a modal of every box with the same stepper.
 */
export function BattlePicks({ title, hint, state }: BattlePicksContent) {
  const id = useId();
  const [picking, setPicking] = useState(false);
  return (
    <section
      aria-labelledby={id}
      aria-busy={state.status === "loading" || undefined}
      className={cardStyles({ radius: "3xl", className: "flex min-w-0 flex-col gap-4 p-5 md:p-7" })}
    >
      <Text as="h2" id={id} variant="heading-h2" tone="primary">
        {title}
      </Text>
      {state.status === "loading" && (
        <div className={GRID}>
          {["a", "b", "c"].map((key) => (
            <Skeleton key={key} className="aspect-[3/5] rounded-xl" />
          ))}
        </div>
      )}
      {state.status === "error" && <LoadError message={state.message} />}
      {state.status === "ready" && (
        <>
          <ol className={GRID}>
            {state.picks.map((pick) => (
              <li key={pick.id} className="flex min-w-0">
                <Pick pick={pick} className="flex-1" />
              </li>
            ))}
            <li className="flex min-w-0">
              <Modal
                open={picking}
                onOpenChange={setPicking}
                title={state.add.title}
                description={state.add.description}
                size="lg"
                trigger={
                  <button
                    type="button"
                    className={cn(
                      "flex min-h-48 flex-1 cursor-pointer flex-col items-center justify-center gap-2 rounded-xl border border-border-strong border-dashed font-body text-body-b2-strong text-accent-text hover:bg-bg-hover",
                      FOCUS_RING,
                    )}
                  >
                    <Icon icon={plusIcon} />
                    {state.add.label}
                  </button>
                }
                footer={
                  <ModalFooter>
                    <ModalClose render={<Button variant="primary" />}>
                      {state.add.doneLabel}
                    </ModalClose>
                  </ModalFooter>
                }
              >
                <ul className="grid grid-cols-2 gap-3 sm:grid-cols-3">
                  {state.add.boxes.map((box) => (
                    <li key={box.id} className="flex min-w-0">
                      <Pick pick={box} className="flex-1" />
                    </li>
                  ))}
                </ul>
              </Modal>
            </li>
          </ol>
          {state.picks.length === 0 && (
            <Text as="p" variant="body-b2" tone="secondary">
              {state.emptyMessage}
            </Text>
          )}
        </>
      )}
      <Text as="p" variant="body-b3" tone="tertiary">
        {hint}
      </Text>
    </section>
  );
}

function Pick({ pick, className }: { pick: BattleBoxPickContent; className?: string }) {
  const { id: _id, earlier, later, remove, ...props } = pick;
  const controls = [
    earlier && { control: earlier, icon: arrowLeftIcon },
    later && { control: later, icon: arrowRightIcon },
    remove && { control: remove, icon: trashIcon },
  ].filter(Boolean) as { control: PickControl; icon: typeof plusIcon }[];
  return (
    <BattleBoxPick
      {...props}
      className={className}
      actions={
        controls.length > 0
          ? controls.map(({ control, icon }) => (
              <IconButton
                key={control.label}
                icon={icon}
                label={control.label}
                variant="ghost"
                size="sm"
                disabled={control.disabled}
                onClick={control.onPress}
              />
            ))
          : undefined
      }
    />
  );
}
